import { useQuery } from '@tanstack/react-query'
import { NavLink } from 'react-router-dom'

import { useAdminApiClient } from '../api/AdminApiClientContext'
import { queryKeys } from '../api/queryKeys'
import type { HealthResponse, OperatorRole } from '../api/types'
import { useAuth } from '../auth/AuthProvider'

interface NavItem {
  to: string
  label: string
  end: boolean
  badgeKey:
    | 'death_confirmation_attention'
    | 'delivery_problems'
    | 'physical_mail_dispatch'
    | null
  // Minimum role required to see (and navigate to) this entry. Pages
  // role-gate themselves too; this just hides the link when the
  // operator has no business clicking it.
  minRole?: OperatorRole
}

const NAV: readonly NavItem[] = [
  { to: '/', label: 'Health', end: true, badgeKey: null },
  {
    to: '/death-confirmation',
    label: 'Death-confirmation queue',
    end: false,
    badgeKey: 'death_confirmation_attention',
  },
  {
    to: '/deliveries',
    label: 'Delivery problems',
    end: false,
    badgeKey: 'delivery_problems',
  },
  {
    to: '/physical-mail',
    label: 'Physical mail dispatch',
    end: false,
    badgeKey: 'physical_mail_dispatch',
  },
  {
    to: '/lifecycle',
    label: 'Account lifecycle',
    end: false,
    badgeKey: null,
  },
  { to: '/audit', label: 'Audit log', end: false, badgeKey: null },
  {
    to: '/daemon',
    label: 'Daemon Inspector',
    end: false,
    badgeKey: null,
    minRole: 'OPERATOR',
  },
  {
    to: '/operators',
    label: 'Operators',
    end: false,
    badgeKey: null,
    minRole: 'ADMIN',
  },
]

export function Sidebar() {
  const client = useAdminApiClient()
  const auth = useAuth()
  // Subscribe to the health snapshot so the sidebar shows live counts.
  // Reuse the HealthPage's queryKey so a single fetch satisfies both
  // surfaces. `enabled: false` here would skip the fetch entirely if
  // /health hasn't been visited; we want the badges to populate on
  // first paint, so we let the sidebar drive the fetch and the page
  // hits the cache when the operator navigates there.
  const query = useQuery({
    queryKey: queryKeys.health(),
    queryFn: () => client.get<HealthResponse>('/r/admin/health'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  })
  const badges = query.data?.success
    ? query.data.data.operator_queue_badges
    : null

  return (
    <nav
      aria-label="Main navigation"
      className="flex h-full w-64 shrink-0 flex-col border-r border-stone-200
                 bg-white"
    >
      <div className="px-5 py-5">
        <span className="block text-lg font-semibold text-stone-900">
          My Last Message
        </span>
        <span className="block text-xs uppercase tracking-wide text-stone-500">
          Admin console
        </span>
      </div>
      <ul className="flex-1 space-y-1 px-2">
        {NAV.filter((item) =>
          !item.minRole || auth.requireRole(item.minRole),
        ).map((item) => {
          const count = item.badgeKey && badges ? badges[item.badgeKey] : null
          return (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  [
                    'flex items-center justify-between rounded-md px-3 py-2',
                    'text-sm font-medium',
                    isActive
                      ? 'bg-accent/10 text-accent'
                      : 'text-stone-700 hover:bg-stone-100',
                  ].join(' ')
                }
              >
                <span>{item.label}</span>
                {count !== null && count > 0 && (
                  <span
                    className="rounded-full bg-amber-100 px-2 py-0.5 text-xs
                               font-semibold text-amber-900"
                    data-testid={`sidebar-badge-${item.to}`}
                  >
                    {count}
                  </span>
                )}
              </NavLink>
            </li>
          )
        })}
      </ul>
      <div className="border-t border-stone-200 px-5 py-3 text-xs text-stone-400">
        Wave 2 — audited interventions
      </div>
    </nav>
  )
}
